import api from './axios';
import { getStoredToken } from '../utils/authStorage';

export interface LedgerAgentSessionMessage {
    id: string;
    role: 'user' | 'assistant';
    text: string;
    meta?: Record<string, unknown>;
    created_at?: string;
}

export interface LedgerAgentSession {
    _id: string;
    business_id: string;
    user_id: string;
    title: string;
    status: string;
    messages: LedgerAgentSessionMessage[];
    active_job_id?: string | null;
    active_checkpoint_id?: string | null;
    working_memory?: {
        last_result_set?: {
            transaction_ids?: string[];
            document_ids?: string[];
            filters?: { label: string; value: string }[];
            [key: string]: unknown;
        } | null;
        pending_operation?: Record<string, unknown> | null;
        clarification_state?: Record<string, unknown> | null;
        last_focus?: {
            source?: string;
            transaction_ids?: string[];
            document_ids?: string[];
            filters?: { label: string; value: string }[];
            updated_at?: string;
            [key: string]: unknown;
        } | null;
        last_plan?: {
            route?: string;
            action?: string;
            instruction?: string;
            updated_at?: string;
            [key: string]: unknown;
        } | null;
        last_turn_trace?: {
            raw_instruction?: string;
            route?: string;
            interpretation?: Record<string, unknown>;
            normalized_intent?: Record<string, unknown>;
            planner?: Record<string, unknown>;
            observations?: Record<string, unknown>;
            tool_calls?: string[];
            tool_observations?: Record<string, unknown>;
            final_action?: string | null;
            clarification?: Record<string, unknown>;
            result_summary?: string | null;
            created_at?: string;
            [key: string]: unknown;
        } | null;
        [key: string]: unknown;
    };
    created_at?: string;
    updated_at?: string;
}

export interface LedgerAgentJobEvent {
    id: string;
    stage: string;
    message: string;
    meta?: {
        tools?: string[];
        tool_count?: number;
        work_item_id?: string;
        source?: string;
        reasoning?: string;
        [key: string]: unknown;
    };
    created_at?: string;
}

export interface LedgerAgentJob {
    _id: string;
    session_id: string;
    business_id: string;
    status: string;
    spec?: Record<string, unknown>;
    progress_events?: LedgerAgentJobEvent[];
    stats?: Record<string, number>;
    result?: Record<string, unknown> | null;
    control?: {
        interrupt_requested?: boolean;
        interrupt_message?: string | null;
        paused?: boolean;
    };
    created_at?: string;
    updated_at?: string;
    started_at?: string | null;
    finished_at?: string | null;
}

export interface LedgerAgentCandidateEvidence {
    kind: string;
    label: string;
    source_id: string;
    confidence?: number;
    meta?: Record<string, unknown>;
}

export interface LedgerAgentCandidate {
    candidate_id: string;
    vendor_name?: string;
    score?: number;
    summary?: string;
    evidence?: LedgerAgentCandidateEvidence[];
    finz_data?: {
        fiscal_bucket?: string;
        account_name?: string;
    };
}

export interface LedgerAgentToolPlan {
    source?: string;
    reasoning?: string;
}

export interface LedgerAgentBusinessMemory {
    vendor_name?: string;
    last_resolution_status?: string;
    preferred_mapping?: {
        fiscal_bucket?: string;
        account_name?: string;
    };
    [key: string]: unknown;
}

export interface LedgerAgentAction {
    label: string;
    action_type: string;
    payload?: Record<string, unknown>;
    variant?: string;
}

export interface LedgerAgentOpenNormalizationEditorPayload {
    transaction_id?: string;
    suggested_match_type?: string;
    suggested_match_value?: string;
    suggested_normalized_description?: string;
    suggested_vendor_name?: string;
}

export interface LedgerAgentCheckpoint {
    _id: string;
    session_id: string;
    job_id: string;
    status: string;
    checkpoint_type: string;
    prompt: string;
    payload?: {
        candidates?: LedgerAgentCandidate[];
        actions?: LedgerAgentAction[];
        conflicts?: string[];
        recommended_question?: string | null;
        tool_catalog?: { name: string; purpose: string }[];
        business_memory?: LedgerAgentBusinessMemory;
        candidate_bundle?: {
            selected_tools?: string[];
            tool_plan?: LedgerAgentToolPlan;
            [key: string]: unknown;
        };
        [key: string]: unknown;
    };
    response?: Record<string, unknown> | null;
    created_at?: string;
    updated_at?: string;
}

export interface LedgerAgentHistorySession {
    session_id: string;
    title: string;
    preview: string;
    last_updated: string;
    message_count?: number;
}

const ledgerAgentApi = {
    createSession: async (
        message: string,
        sessionId?: string | null,
        businessId?: string,
    ): Promise<{ session: LedgerAgentSession; job: LedgerAgentJob }> => {
        const config = businessId ? { params: { business_id: businessId } } : {};
        const response = await api.post(
            '/ledger/agent/sessions',
            { message, session_id: sessionId || null },
            config,
        );
        return response.data;
    },

    sendMessage: async (
        sessionId: string,
        message: string,
        businessId?: string,
    ): Promise<{ session: LedgerAgentSession; job: LedgerAgentJob }> => {
        const config = businessId ? { params: { business_id: businessId } } : {};
        const response = await api.post(
            `/ledger/agent/sessions/${sessionId}/messages`,
            { message, session_id: sessionId },
            config,
        );
        return response.data;
    },

    getSession: async (
        sessionId: string,
    ): Promise<{ session: LedgerAgentSession; jobs: LedgerAgentJob[]; checkpoint?: LedgerAgentCheckpoint | null }> => {
        const normalized = String(sessionId || '').trim().toLowerCase();
        if (!normalized || normalized === 'null' || normalized === 'undefined') {
            throw new Error('Invalid ledger agent session id');
        }
        const response = await api.get(`/ledger/agent/sessions/${sessionId}`);
        return response.data;
    },

    streamSession: (
        sessionId: string,
        handlers: {
            onSnapshot: (payload: { session: LedgerAgentSession; jobs: LedgerAgentJob[]; checkpoint?: LedgerAgentCheckpoint | null }) => void;
            onError?: (error: Error) => void;
        },
    ): (() => void) => {
        const controller = new AbortController();
        const token = getStoredToken();
        const baseUrl = String(process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
        const url = `${baseUrl}/ledger/agent/sessions/${sessionId}/stream`;

        const run = async () => {
            try {
                const response = await fetch(url, {
                    method: 'GET',
                    headers: {
                        ...(token ? { Authorization: `Bearer ${token}` } : {}),
                        Accept: 'text/event-stream',
                    },
                    signal: controller.signal,
                });
                if (!response.ok || !response.body) {
                    throw new Error(`Ledger agent stream failed (${response.status})`);
                }

                const reader = response.body.getReader();
                const decoder = new TextDecoder();
                let buffer = '';

                while (true) {
                    const { value, done } = await reader.read();
                    if (done) break;
                    buffer += decoder.decode(value, { stream: true });
                    const chunks = buffer.split('\n\n');
                    buffer = chunks.pop() || '';

                    chunks.forEach((chunk) => {
                        const lines = chunk.split('\n');
                        let eventName = 'message';
                        const dataLines: string[] = [];

                        lines.forEach((line) => {
                            if (line.startsWith('event:')) {
                                eventName = line.slice(6).trim();
                            } else if (line.startsWith('data:')) {
                                dataLines.push(line.slice(5).trim());
                            }
                        });

                        if (eventName !== 'session_snapshot' || dataLines.length === 0) return;

                        try {
                            const payload = JSON.parse(dataLines.join('\n')) as {
                                session: LedgerAgentSession;
                                jobs: LedgerAgentJob[];
                                checkpoint?: LedgerAgentCheckpoint | null;
                            };
                            handlers.onSnapshot(payload);
                        } catch (error) {
                            handlers.onError?.(error instanceof Error ? error : new Error('Failed to parse ledger agent stream event'));
                        }
                    });
                }
            } catch (error) {
                if (controller.signal.aborted) return;
                handlers.onError?.(error instanceof Error ? error : new Error('Ledger agent stream failed'));
            }
        };

        void run();

        return () => controller.abort();
    },

    listSessions: async (
        businessId?: string,
        search?: string,
    ): Promise<{ sessions: LedgerAgentHistorySession[] }> => {
        const params: Record<string, string> = {};
        if (businessId) params.business_id = businessId;
        if (search && search.trim()) params.search = search.trim();
        const response = await api.get('/ledger/agent/sessions', { params });
        return response.data;
    },

    getJob: async (jobId: string): Promise<LedgerAgentJob> => {
        const response = await api.get(`/ledger/agent/jobs/${jobId}`);
        return response.data;
    },

    interruptJob: async (jobId: string, message?: string): Promise<LedgerAgentJob> => {
        const response = await api.post(`/ledger/agent/jobs/${jobId}/interrupt`, { message });
        return response.data;
    },
};

export default ledgerAgentApi;
